import { textColorFields } from '../_shared/textColor.js';
import { animationFields, mobilePaddingFields, paddingFields, sectionChromeFields, } from '../_shared/universal.js';
import { mediaOverlayFields, mobileMediaOverlayFields } from '../_shared/mediaOverlay.js';
import { CAPTION_GROUP, mediaCaptionFields, mobileMediaCaptionFields, } from '../_shared/mediaCaption.js';
import { DESKTOP_GROUP, MOBILE_GROUP } from '../_shared/device.js';
import { ASPECT_RATIO_OPTIONS, MOBILE_ASPECT_INFO, mobileAspectOptions, } from '../_shared/aspect.js';
/**
 * image — ONE image, and nothing else.
 *
 * The registry already carries five image-bearing sections (image-with-text, image-slider, collage,
 * slideshow, shoppable-image) and every one of them wraps the picture in something: a heading, a carousel
 * engine, a grid, hotspots. The single most common storefront a seller actually builds is a STACK of
 * ready-made visuals — the whole sales page exported as five or ten tall JPEGs — and until now that seller
 * had to fight a slider or a collage to get it. This section is the missing primitive: pick an image, choose
 * how wide it sits, stack the section as many times as there are images.
 *
 * Deliberately NOT here: heading, subheading, buttons. Anything that needs a block of words next to the
 * picture already has a section (image-with-text for a side-by-side, hero/parallax-hero for a page opener).
 *
 * ONE line of words DID come back, on 19/09/2026, and the founder's reason is the one that settles it: a
 * seller stacking ten exported visuals does not want a different section the moment one of them needs a
 * sentence written on it. So the caption is a SETTING — `mediaCaptionText` plus the nine-spot placement
 * grid — and it paints nothing at all while it is empty, which is what keeps this section fast to use ten
 * times on one page.
 *
 * The defaults are tuned for the stack, not for a lone banner: full page width, zero top/bottom padding, so
 * two of these placed one after the other touch edge to edge with no seam. A seller who wants air between
 * them raises the padding; the reverse (hunting down 48px of default padding on ten sections) is the chore
 * we refuse to hand them.
 *
 * ## TWO DEVICES, TWO DESIGNS — and two PARTS in the inspector (18/09/2026)
 *
 * The settings used to be one list called "Image" with three phone escape hatches hidden inside it
 * (`mobileImage`, `mobileAspect`) and two more parked at the very bottom under "Section padding (mobile)".
 * Founder, 18/09: the phone must be able to have ITS OWN design, and the settings for it must be findable.
 *
 * So the list is now cut where the reader changes: what the picture IS (file, link, which devices show it)
 * sits at the top, then everything that decides how it LOOKS under "Desktop", then the same list again under
 * "Mobile". Every mobile control means "same as desktop" while it is untouched — `inherit` on the selects,
 * an absent value on the sliders (see `RangeField.inheritFrom`) — so this re-cut repaints nothing on a
 * storefront that is already live, and a seller who never opens the Mobile part keeps one design everywhere.
 */
/** "The file decides" — named once, because the desktop list and the mobile list both open with it. */
const NATURAL = { value: 'natural', label: 'Natural (no crop)' };
/** The picture itself: the file, where it goes when clicked, who sees it. One answer for both readers. */
const IMAGE = 'Image';
export const imageSchema = {
    type: 'image',
    name: 'Image',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── The picture (shared by both devices) ─────────────────────────────────────
        {
            id: 'image',
            type: 'image',
            label: 'Image',
            group: IMAGE,
            info: 'The alt text you set on the image is what search engines and screen readers read.',
        },
        {
            id: 'link',
            type: 'link',
            label: 'Link',
            group: IMAGE,
            info: 'Makes the whole image clickable.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            group: IMAGE,
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Text over the media (shared by both devices — see _shared/mediaCaption.ts) ─
        ...mediaCaptionFields(CAPTION_GROUP, 'middle-center'),
        // ── Desktop ──────────────────────────────────────────────────────────────────
        {
            id: 'aspect',
            type: 'select',
            label: 'Shape',
            group: DESKTOP_GROUP,
            default: 'natural',
            aiConfigurable: true,
            info: 'Natural keeps the file’s own proportions — a 1000×6000 image stays 1000×6000. Any other shape crops the image to match, which is what makes a stack of differently-sized files line up.',
            options: [NATURAL, ...ASPECT_RATIO_OPTIONS],
        },
        {
            id: 'imageWidth',
            type: 'range',
            label: 'Image width',
            group: DESKTOP_GROUP,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 100,
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: DESKTOP_GROUP,
            default: 'center',
            aiConfigurable: true,
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
            visibleWhen: { field: 'imageWidth', notEquals: 100 },
        },
        {
            id: 'borderRadius',
            type: 'range',
            label: 'Corner radius',
            group: DESKTOP_GROUP,
            min: 0,
            max: 60,
            step: 2,
            unit: 'px',
            default: 0,
        },
        // The overlay (2026-09-18): a tint or a fade over the picture, so a clean photo can dissolve into the
        // band below it. Shared engine — see _shared/mediaOverlay.ts for why it is a setting and not a re-export.
        ...mediaOverlayFields(DESKTOP_GROUP),
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // Padding moves INTO the device part it belongs to (18/09): a seller setting up the phone should find
        // the phone's margins with the phone's other settings, not two groups further down the panel.
        ...paddingFields(DESKTOP_GROUP),
        // ── Mobile ───────────────────────────────────────────────────────────────────
        {
            id: 'mobileImage',
            type: 'image',
            // "Image", not "Mobile image": the part it sits under already says Mobile, and a label that repeats
            // its own heading is the noise that made these settings hard to find in the first place.
            label: 'Image',
            group: MOBILE_GROUP,
            info: 'Optional. Shown under 750px instead of the desktop image — leave empty to use the same one.',
        },
        // The SAME picture, a different crop on a phone — see _shared/aspect.ts for why a ratio is not a
        // device-independent choice, and why this one cannot be an inline style.
        {
            id: 'mobileAspect',
            type: 'select',
            label: 'Shape',
            group: MOBILE_GROUP,
            default: 'inherit',
            aiConfigurable: true,
            info: MOBILE_ASPECT_INFO,
            options: mobileAspectOptions(NATURAL),
        },
        {
            id: 'mobileImageWidth',
            type: 'range',
            label: 'Image width',
            group: MOBILE_GROUP,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            inheritFrom: 'imageWidth',
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Alignment',
            group: MOBILE_GROUP,
            default: 'inherit',
            aiConfigurable: true,
            options: [
                { value: 'inherit', label: 'Same as desktop' },
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'mobileBorderRadius',
            type: 'range',
            label: 'Corner radius',
            group: MOBILE_GROUP,
            min: 0,
            max: 60,
            step: 2,
            unit: 'px',
            inheritFrom: 'borderRadius',
        },
        ...mobileMediaOverlayFields(MOBILE_GROUP),
        ...mobileMediaCaptionFields(MOBILE_GROUP),
        ...mobilePaddingFields(MOBILE_GROUP),
        // ── Section (the band itself — one answer for both readers) ──────────────────
        ...sectionChromeFields,
        ...animationFields,
    ],
};
export const imagePresets = [
    {
        // No demo image on purpose: the section is added to hold the seller's OWN visual, and a placeholder
        // frame that says "drop an image here" is a clearer invitation than a stock photo they must remember
        // to replace. Padding is 0 so consecutive images stack seamlessly (see the schema note).
        name: 'Image',
        settings: {
            aspect: 'natural',
            mobileAspect: 'inherit',
            imageWidth: 100,
            alignment: 'center',
            borderRadius: 0,
            mediaOverlayStyle: 'none',
            // No caption text: the section is still the wordless primitive until the seller writes on it.
            mediaCaptionPosition: 'middle-center',
            mediaCaptionBackdrop: 'shadow',
            // Every mobile setting starts UNSET on purpose: an unset mobile value means "same as desktop", so a
            // section dropped on a page has one design until the seller decides the phone needs its own.
            mobileAlignment: 'inherit',
            mobileMediaOverlayStyle: 'inherit',
            mobileMediaCaptionPosition: 'inherit',
            visibility: 'always-display',
            colorScheme: 'light',
            width: 'full',
            paddingTop: 0,
            paddingBottom: 0,
            mobilePaddingTop: 0,
            mobilePaddingBottom: 0,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const imageMeta = {
    label: 'Image',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map